import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import {
  Button,
  ButtonType,
  Card,
  Icon,
  IconLookup,
  SelectOption,
} from 'component-library-and-tools';

import Select from 'shared/components/Select';
import SelectableCard from 'shared/components/SelectableCard';
import TextField from 'shared/components/TextField';
import { IndicatorType } from 'shared/types/transformation';
import { ENTER_KEY, DARK_THEME } from 'shared/constants';
import { SafelistTerm } from 'shared/types/safelist';
import { colors } from 'lib/prisma-styles';

import { SELECT_TYPE_OPTIONS } from '../../../../constants';

import { RedactableTermProps, InEditRedactableTermProps } from './types';

const termCardStyle = `
  width: 100%;
  min-width: 30rem;
  justify-content: space-between;
  padding: 0;
`;

const StyledSelectableCard = styled(SelectableCard)`
  && {
    ${termCardStyle}
    background-color: ${colors.background.section};
    padding-left: 1rem;
  }
`;

const StyledRedactableTermContent = styled.div`
  width: 100%;
  min-width: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  margin: 0 0.834rem;
`;

const StyledTermValue = styled.span`
  font-weight: 700;
`;

const StyledIconContainer = styled.div`
  display: inline-flex;
`;

const StyledIcon = styled(Icon)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 3.58rem;
  border-left: 1px solid ${colors.interactive.defaultBorder};
`;

const StyledSelect = styled(Select)`
  width: 10rem;
`;

const RedactableTerm = ({
  term,
  isSelected,
  onSelect,
  onEditTerm,
  onRemoveTerm,
}: RedactableTermProps) => {
  const handleSelection = (isSelected: boolean) => {
    onSelect(term, isSelected);
  };

  const editCurrentTerm = (event: React.MouseEvent) => {
    event.stopPropagation();
    onEditTerm(term, true);
  }

  const deleteTerm = (event: React.MouseEvent) => {
    event.stopPropagation();
    onRemoveTerm(term);
  };

  return (
    <StyledSelectableCard
      isSelected={isSelected}
      onSelect={handleSelection}
    >
      <StyledRedactableTermContent>
        <StyledTermValue>{term.entity}</StyledTermValue>
        <StyledTermValue>{term.type}</StyledTermValue>
      </StyledRedactableTermContent>
      <StyledIconContainer>
        <StyledIcon icon={IconLookup.PENCIL2} onClick={editCurrentTerm} />
        <StyledIcon icon={IconLookup.TRASH} onClick={deleteTerm} />
      </StyledIconContainer>
    </StyledSelectableCard>
  );
};

/*--  RedactableTerm card during edit mode  --*/

const StyledCard = styled(Card)`
  && {
    ${termCardStyle}
    background-color: ${colors.background.section};
    height: 4rem;
  }
`;

const StyledActionButton = styled(Button)`
  && {
    min-width: unset;
    min-height: unset;
    padding: 0;
    cursor: pointer;
    color: inherit;

    ${({ theme }) => theme.mode === DARK_THEME && css`
      &:hover {
        color: ${colors.content.inverted};
      }
    `}
  }
`;

const StyledEditTermContentContainer = styled.div`
  display: flex;
  align-items: center;
  height: 3rem;

  // These rules apply to all its children.
  & > * {
    height: 100%;

    &:not(:last-child) {
      margin-right: 0.5rem;
    }
  }
`;

const isAllowedToSave = (term: SafelistTerm, entity: string, termType: string) => {
  const trimmedEntity = entity.trim();

  return !!trimmedEntity && !!termType && (trimmedEntity !== term.entity || termType !== term.type);
};

const InEditRedactableTerm = ({
  term,
  onCancelEdit,
  onSaveEdit,
}: InEditRedactableTermProps) => {

  // Local State
  const [termType, setTermType] = useState(term.type || IndicatorType.URL);
  const [entity, setEntity] = useState(term.entity || '');

  const saveChanges = () => {
    const updatedTerm = {
      entity: entity.trim(),
      type: termType,
    };

    onSaveEdit(term, updatedTerm);
  };


  const cancelEdit = () => {
    // If this a newly created extracted term and the user cancels, meaning its original entity value is '', then we inform redux to delete that empty entry
    const shouldDeleteTerm = !term.entity;

    onCancelEdit(term, false, shouldDeleteTerm);
  };

  const handleEnterKey = (event: any) => {
    if (event.key === ENTER_KEY && isAllowedToSave(term, entity, termType)) {
      setEntity(event.target.value);
      saveChanges();
    }
  }

  return (
    <StyledCard>
      <StyledRedactableTermContent>
        <StyledEditTermContentContainer>
          <StyledActionButton
            label=""
            icon={IconLookup.CHECK}
            isDisabled={!isAllowedToSave(term, entity, termType)}
            type={ButtonType.NOT_FILLED}
            onClick={saveChanges}
          />
          <StyledActionButton
            label=""
            icon={IconLookup.CROSS}
            type={ButtonType.NOT_FILLED}
            onClick={cancelEdit}
          />
          <TextField
            value={term.entity}
            onKeyPress={handleEnterKey}
            onChange={(e) => setEntity(e.target.value)}
          />
          <StyledSelect
            menuGap={2}
            defaultOptions={[{ name: termType }]}
            options={SELECT_TYPE_OPTIONS}
            onSelect={(selectedOption) => setTermType((selectedOption as SelectOption).name)}
          />
        </StyledEditTermContentContainer>
      </StyledRedactableTermContent>
    </StyledCard>
  )
}


export { RedactableTerm, InEditRedactableTerm };
